<script setup lang="ts" generic="TModel">
import { useRadioGroup } from '~/composables/group';

const props = withDefaults(defineProps<{
  label?: string
  error?: string
  scrollable?: boolean
  hideDetails?: boolean | `auto`
}>(), {
  hideDetails: false,
  scrollable: false,
});

defineSlots<{
  default: () => unknown
}>();

const modelValue = defineModel<TModel>({ required: true });
const hasError = computed(() => !!props.error);
useRadioGroup(modelValue, hasError);

const displayHint = computed(() => {
  if (props.hideDetails === false) return true;
  if (props.hideDetails === true) return false;
  return !!props.error;
});
</script>

<template>
  <div
    :class="{
      'iad-radio-group-scrollable': scrollable,
    }"
  >
    <IadLabel
      :text="label"
      tag="p"
      :error
    />
    <fieldset
      class="inline-flex gap-xs"
      :class="{
        'flex-wrap': !scrollable,
        'flex-nowrap lg:flex-wrap': scrollable,
      }"
    >
      <slot />
    </fieldset>
    <IadInputError
      v-if="displayHint"
      :error
    />
  </div>
</template>

<style scoped>
.iad-radio-group-scrollable {
  /*
    handle scrollbar in windows:
    • -ms-overflow-style isn't working anymore
    • hide scrollbar on firefox
  */
  scrollbar-width: none;
  /*
    use ::-webkit-scrollbar for edge/chrome on windows
    https://developer.mozilla.org/en-US/docs/Web/CSS/::-webkit-scrollbar
    https://www.digitalocean.com/community/tutorials/css-scrollbars
  */
  &::-webkit-scrollbar {
    display: none;
  }

  @apply flex snap-mandatory snap-x scroll-smooth w-full overflow-auto;
}

.iad-radio-group-scrollable :deep(> *) {
  @apply snap-start flex-shrink-0 flex-grow-0 scroll-m-m;
}

@screen lg {
  .iad-radio-group-scrollable {
    @apply flex-wrap;
  }
}
</style>
